// Port of Networking/URLSessionHTTPClient.swift on top of `fetch`.
//
// Per request:
// 1. Resolves the URL against the base URL (the app's origin) and validates it (`http`/`https` with a host) →
//    `invalidURL`.
// 2. Checks connectivity: when offline it throws `offline` immediately, without touching the network, unless the cache
//    policy allows an answer from the HTTP cache (`returnCacheElseLoad`, `cacheOnly`), in which case only the cache
//    is consulted (`only-if-cached`; a miss is `offline`).
// 3. Sends the request (no `User-Agent`: a page cannot set it; no cookies unless the request asks) and maps failures
//    to NetworkError (abort → cancelled, its own timer → timeout, `fetch` TypeError → offline when the browser says it
//    is offline, else transport; statuses the request does not accept → `http` with the parsed `Retry-After`).
//    A 304 answering a request that carries its own `If-Modified-Since`/`If-None-Match` is returned as a response.
// 4. Retries retryable errors following the request's `retryPolicy` (else the client's), waiting with the injected
//    `sleep`. `Retry-After` is honoured; one longer than the policy's `maxDelay` is not waited for and the error is
//    thrown so the caller can reschedule. Non-idempotent methods are only retried after 429/503. `cacheOnly` requests
//    are never retried.
// 5. An aborted `signal` throws `cancelled` and stops retrying; it is checked before every attempt.

import type { EpochMs } from '../core/models';
import type { ConnectivityProviding } from './connectivity';
import {
  acceptsStatus,
  canBeServedOffline,
  fetchCacheMode,
  isIdempotent,
  normalizedMethod,
  type HTTPClient,
  type HTTPRequest,
  type HTTPResponse,
} from './httpTypes';
import { NetworkFailure, networkErrorFrom, retryAfterOf, type NetworkError } from './networkError';
import { RetryAfter, RetryPolicies, retryDelay, shouldRetry, type RetryPolicy } from './retryPolicy';

/** Waits `seconds`; rejects with an AbortError when `signal` aborts. */
export type Sleep = (seconds: number, signal?: AbortSignal) => Promise<void>;

export const defaultSleep: Sleep = (seconds, signal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, Math.max(0, seconds) * 1000);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

export interface FetchHTTPClientOptions {
  /** Defaults to the global `fetch`. */
  fetch?: typeof fetch;
  retryPolicy?: RetryPolicy;
  /** When provided and offline, requests fail fast with `offline`. */
  connectivity?: ConnectivityProviding;
  /** Waits between attempts (seconds). Injected in tests. */
  sleep?: Sleep;
  /** Base for relative URLs; defaults to the page's location. */
  baseURL?: string;
  /** Clock for `Retry-After` HTTP-dates when the response has no `Date` header. */
  now?: () => EpochMs;
  /** Whether the browser reports a network (`navigator.onLine`); used to classify `fetch` TypeErrors. */
  browserOnline?: () => boolean;
}

/** Code of a cache-only miss while online (Swift `URLError.resourceUnavailable`). */
export const resourceUnavailableCode = -1008;

/** A cache-only lookup found nothing: `offline` while offline, otherwise a non-retried transport error. */
function cacheMiss(whileOffline: boolean): NetworkError {
  return whileOffline
    ? { kind: 'offline' }
    : { kind: 'transport', code: resourceUnavailableCode, description: 'Not in the HTTP cache' };
}

/** Headers a page cannot set (fetch would ignore or reject them). */
const droppedHeaders = new Set(['user-agent']);

export class FetchHTTPClient implements HTTPClient {
  readonly retryPolicy: RetryPolicy;
  private readonly fetchImpl: typeof fetch;
  private readonly connectivity?: ConnectivityProviding;
  private readonly sleep: Sleep;
  private readonly baseURL?: string;
  private readonly now: () => EpochMs;
  private readonly browserOnline: () => boolean;

  constructor(options: FetchHTTPClientOptions = {}) {
    this.fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.retryPolicy = options.retryPolicy ?? RetryPolicies.default;
    this.connectivity = options.connectivity;
    this.sleep = options.sleep ?? defaultSleep;
    this.baseURL = options.baseURL ?? (globalThis as { location?: { href?: string } }).location?.href;
    this.now = options.now ?? Date.now;
    this.browserOnline =
      options.browserOnline ?? (() => (globalThis.navigator as { onLine?: boolean } | undefined)?.onLine !== false);
  }

  async send(request: HTTPRequest): Promise<HTTPResponse> {
    const url = this.resolve(request.url);
    const policy = request.retryPolicy ?? this.retryPolicy;
    let attempt = 1;
    for (;;) {
      if (request.signal?.aborted) throw new NetworkFailure({ kind: 'cancelled' });

      const isOffline = this.connectivity ? !this.connectivity.isOnline : false;
      if (isOffline && !canBeServedOffline(request.cachePolicy)) throw new NetworkFailure({ kind: 'offline' });

      let error: NetworkError;
      try {
        return await this.perform(request, url, isOffline);
      } catch (caught) {
        error = networkErrorFrom(caught);
      }

      if (error.kind === 'cancelled') throw new NetworkFailure(error);
      if (!this.shouldRetry(request, policy, error, attempt)) throw new NetworkFailure(error);
      if (request.signal?.aborted) throw new NetworkFailure({ kind: 'cancelled' });

      const delay = retryDelay(policy, attempt, retryAfterOf(error));
      try {
        await this.sleep(delay, request.signal);
      } catch (sleepError) {
        throw new NetworkFailure(request.signal?.aborted ? { kind: 'cancelled' } : networkErrorFrom(sleepError));
      }
      attempt += 1;
    }
  }

  /** Absolute URL, or `invalidURL`. */
  resolve(raw: string): string {
    // An empty authority ("https:///path") has no host; WHATWG URL parsing would silently take the path as the host.
    if (/^[a-z][a-z0-9+.-]*:\/\/\//i.test(raw)) throw new NetworkFailure({ kind: 'invalidURL' });
    let parsed: URL;
    try {
      parsed = this.baseURL !== undefined ? new URL(raw, this.baseURL) : new URL(raw);
    } catch {
      throw new NetworkFailure({ kind: 'invalidURL' });
    }
    if ((parsed.protocol !== 'http:' && parsed.protocol !== 'https:') || parsed.hostname === '') {
      throw new NetworkFailure({ kind: 'invalidURL' });
    }
    return parsed.href;
  }

  private shouldRetry(request: HTTPRequest, policy: RetryPolicy, error: NetworkError, attempt: number): boolean {
    if (request.cachePolicy === 'cacheOnly' || !shouldRetry(policy, error, attempt)) return false;
    if (isIdempotent(request)) return true;
    // A POST/PATCH may have been processed before a timeout or transport failure: only retry when the server
    // explicitly declined it.
    return error.kind === 'http' && (error.status === 429 || error.status === 503);
  }

  private async perform(
    request: HTTPRequest,
    url: string,
    cacheLookupOnlyWhileOffline: boolean,
  ): Promise<HTTPResponse> {
    const controller = new AbortController();
    let timedOut = false;
    const timeoutSeconds = Number.isFinite(request.timeout) && request.timeout > 0 ? request.timeout : 20;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutSeconds * 1000);
    const onAbort = () => controller.abort();
    request.signal?.addEventListener('abort', onAbort, { once: true });

    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(request.headers)) {
      if (!droppedHeaders.has(name.toLowerCase())) headers[name] = value;
    }
    const cache = fetchCacheMode(request.cachePolicy, cacheLookupOnlyWhileOffline);
    try {
      let response: Response;
      try {
        response = await this.fetchImpl(url, {
          method: normalizedMethod(request),
          headers,
          body: request.body as BodyInit | undefined,
          cache,
          // `only-if-cached` is only allowed for same-origin requests.
          mode: cache === 'only-if-cached' ? 'same-origin' : 'cors',
          credentials: request.credentials ?? 'omit',
          redirect: 'follow',
          signal: controller.signal,
        });
      } catch (error) {
        if (cache === 'only-if-cached' && !request.signal?.aborted && !timedOut) {
          throw new NetworkFailure(cacheMiss(cacheLookupOnlyWhileOffline));
        }
        throw new NetworkFailure(this.mapFetchError(error, request, timedOut, cacheLookupOnlyWhileOffline));
      }
      // Some engines answer an `only-if-cached` miss with a synthetic 504 instead of a network error.
      if (cache === 'only-if-cached' && response.status === 504) {
        throw new NetworkFailure(cacheMiss(cacheLookupOnlyWhileOffline));
      }
      if (response.type === 'opaque' || response.type === 'opaqueredirect' || response.status === 0) {
        throw new NetworkFailure({ kind: 'invalidResponse' });
      }
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, name) => {
        responseHeaders[name] = value;
      });
      if (!acceptsStatus(request, response.status)) {
        const retryAfter = RetryAfter.fromHeaders(
          response.headers.get('Retry-After') ?? undefined,
          response.headers.get('Date') ?? undefined,
          this.now,
        );
        // Release the connection; the body of an error answer is not needed.
        response.body?.cancel().catch(() => undefined);
        throw new NetworkFailure({ kind: 'http', status: response.status, retryAfter });
      }
      let data: Uint8Array;
      try {
        data = new Uint8Array(await response.arrayBuffer());
      } catch (error) {
        throw new NetworkFailure(this.mapFetchError(error, request, timedOut, false));
      }
      return { data, statusCode: response.status, headers: responseHeaders, url: response.url || url };
    } finally {
      clearTimeout(timer);
      request.signal?.removeEventListener('abort', onAbort);
    }
  }

  private mapFetchError(
    error: unknown,
    request: HTTPRequest,
    timedOut: boolean,
    cacheOnlyWhileOffline: boolean,
  ): NetworkError {
    if (request.signal?.aborted) return { kind: 'cancelled' };
    if (timedOut) return { kind: 'timeout' };
    const mapped = networkErrorFrom(error);
    if (mapped.kind === 'cancelled') return mapped;
    // Offline cache lookup that found nothing: the resource is simply not available offline.
    if (cacheOnlyWhileOffline) return { kind: 'offline' };
    // fetch gives no error code: a TypeError while the browser reports no network is "offline".
    if (mapped.kind === 'transport' && error instanceof TypeError && !this.browserOnline()) return { kind: 'offline' };
    return mapped;
  }
}
